import React from "react";

const PREVIEW_SRC = "/assets/profile_unlock.png";

const PROFILE_UNLOCK_RUBY_COST = 1;
/** My profile (view) — compact strip; keep small so the bar stays light. */
const UNLOCK_PREVIEW_HEIGHT_DEFAULT_PX = 118;
const UNLOCK_PREVIEW_WIDTH_DEFAULT_PX = Math.round((320 * UNLOCK_PREVIEW_HEIGHT_DEFAULT_PX) / 200);
/** Edit profile — larger hero-style preview (slightly smaller than max for balance). */
const UNLOCK_PREVIEW_HEIGHT_EDIT_PX = 200;
const UNLOCK_PREVIEW_WIDTH_EDIT_PX = Math.round((320 * UNLOCK_PREVIEW_HEIGHT_EDIT_PX) / 200);
const UNLOCK_CTA_BORDER = "#00AEEF";
const unlockBannerPromoTypeClass =
  "font-anton text-[12px] font-normal uppercase leading-[15px] tracking-normal not-italic [leading-trim:none]";
const unlockBannerButtonTypeClass =
  "font-anton text-[12px] font-normal uppercase leading-none tracking-normal not-italic [leading-trim:none]";

export type ProfileUnlockBannerVariant = "default" | "edit";

function openShopDrawer() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("openShopDrawer"));
  }
}

/** Compact single-row strip for My profile (view mode). */
function ProfileUnlockBannerDefault() {
  return (
    <button
      type="button"
      onClick={openShopDrawer}
      className="relative z-[6] mb-0 w-full cursor-pointer overflow-visible border-0 bg-transparent px-0 pt-0 pb-[1.65rem] text-left transition-opacity hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40 sm:pb-[2.1rem]"
      aria-label="Open shop to unlock custom username and premium avatars"
    >
      <div className="relative flex h-[42px] min-h-[42px] max-h-[42px] shrink-0 items-center gap-2 bg-[#583484] pl-3 pr-[min(38vw,9.5rem)] sm:h-auto sm:max-h-none sm:min-h-[42px] sm:py-1.5 sm:pr-[11.5rem] md:min-h-[44px] md:py-2">
        <span
          className="shrink-0 select-none text-[17px] leading-none drop-shadow-sm sm:text-[19px] md:text-[21px]"
          aria-hidden
        >
          🔒
        </span>
        <p className="min-w-0 flex-1 font-anton text-[11px] font-normal uppercase leading-[13px] tracking-normal text-white [leading-trim:none] sm:text-[12px] sm:leading-[15px] md:text-[13px] md:leading-[17px]">
          Unlock <span className="text-[#D2FF00]">custom username</span> and{" "}
          <span className="text-[#D2FF00]">premium avatars</span>!
        </p>
        <img
          src={PREVIEW_SRC}
          alt=""
          width={UNLOCK_PREVIEW_WIDTH_DEFAULT_PX}
          height={UNLOCK_PREVIEW_HEIGHT_DEFAULT_PX}
          className="pointer-events-none absolute bottom-0 right-0 z-10 w-auto max-w-[min(48vw,9.25rem)] origin-bottom object-contain object-bottom drop-shadow-[0_6px_18px_rgba(0,0,0,0.35)] translate-x-2 translate-y-[20%] sm:right-0 sm:max-w-[10.5rem] sm:translate-x-3 sm:translate-y-[26%]"
          style={{ height: UNLOCK_PREVIEW_HEIGHT_DEFAULT_PX }}
          decoding="async"
        />
      </div>
    </button>
  );
}

/** Taller block with inline lime CTA — Edit profile only. */
function ProfileUnlockBannerEdit() {
  return (
    <button
      type="button"
      onClick={openShopDrawer}
      className="relative z-[6] mb-0 mt-2 w-full cursor-pointer overflow-visible border-0 bg-transparent px-0 pt-1 pb-[2.65rem] text-left transition-opacity hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40 sm:mt-3 sm:pb-[3.35rem]"
      aria-label="Open shop to unlock custom username and premium avatars"
    >
      <div className="relative shrink-0 bg-[#583484] pl-3 pr-[min(38vw,9.5rem)] py-2 sm:pr-[11.5rem]">
        <div className="relative z-20 flex gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p className={`text-white ${unlockBannerPromoTypeClass}`}>
              Unlock <span className="text-[#D2FF00]">custom username</span> and
              <br />
              <span className="text-[#D2FF00]">premium avatars</span>!
            </p>
            <div
              className="relative box-border flex h-[30px] min-h-[30px] max-h-[30px] w-[144px] min-w-[144px] max-w-[144px] shrink-0 items-center overflow-hidden border-2 bg-[#d2ff00]"
              style={{ borderColor: UNLOCK_CTA_BORDER }}
            >
              <span
                className="flex h-full shrink-0 items-center pl-1.5 text-[15px] leading-none sm:pl-2"
                aria-hidden
              >
                🔓
              </span>
              <span
                className={`flex h-full min-w-0 flex-1 items-center justify-center px-1.5 text-center text-[#1b062f] ${unlockBannerButtonTypeClass}`}
              >
                Profile unlock
              </span>
              <span className="flex h-full shrink-0 items-center gap-0.5 pr-1.5 sm:pr-2">
                <img
                  src="/assets/currency-ruby.png"
                  alt=""
                  className="h-3.5 w-3.5 object-contain sm:h-4 sm:w-4"
                  width={16}
                  height={16}
                  aria-hidden
                />
                <span className={`tabular-nums text-[#1b062f] ${unlockBannerButtonTypeClass}`}>
                  {PROFILE_UNLOCK_RUBY_COST}
                </span>
              </span>
            </div>
          </div>
        </div>
        <img
          src={PREVIEW_SRC}
          alt=""
          width={UNLOCK_PREVIEW_WIDTH_EDIT_PX}
          height={UNLOCK_PREVIEW_HEIGHT_EDIT_PX}
          className="pointer-events-none absolute bottom-0 right-0 z-10 w-auto max-w-[min(68vw,17rem)] origin-bottom object-contain object-bottom drop-shadow-[0_10px_28px_rgba(0,0,0,0.4)] translate-x-3 translate-y-[26%] sm:right-0 sm:max-w-[18.5rem] sm:translate-x-4 sm:translate-y-[31%]"
          style={{ height: UNLOCK_PREVIEW_HEIGHT_EDIT_PX }}
          decoding="async"
        />
      </div>
    </button>
  );
}

/**
 * Promotional strip when profile name/avatar editing is locked.
 * - `default`: compact row for My profile.
 * - `edit`: purple block with promo + lime “Profile unlock” CTA for Edit profile.
 */
export function ProfileUnlockBanner({ variant = "default" }: { variant?: ProfileUnlockBannerVariant }) {
  return variant === "edit" ? <ProfileUnlockBannerEdit /> : <ProfileUnlockBannerDefault />;
}
